import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { InventoryItem, JobEquipment } from "../types/database";

interface EquipmentAssignmentProps {
  selectedEquipment: JobEquipment[];
  onEquipmentChange: (equipment: JobEquipment[]) => void;
  jobId?: string;
}

export default function EquipmentAssignment({
  selectedEquipment,
  onEquipmentChange,
}: EquipmentAssignmentProps) {
  const { currentOrganization } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Fetch inventory data
  const {
    data: inventoryItems = [],
    isLoading,
    error,
  } = useQuery<InventoryItem[]>({
    queryKey: ["inventory", currentOrganization?.id],
    queryFn: () => inventoryService.getInventoryItems(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = new Set<string>();
    inventoryItems.forEach((item) => {
      if (item.category) {
        uniqueCategories.add(item.category);
      }
    });
    return Array.from(uniqueCategories).sort();
  }, [inventoryItems]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    if (!searchTerm && !selectedCategory) {
      return [];
    }

    return inventoryItems.filter((item) => {
      const matchesSearch = searchTerm
        ? item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.description &&
            item.description.toLowerCase().includes(searchTerm.toLowerCase()))
        : false;

      const matchesCategory = selectedCategory
        ? item.category === selectedCategory
        : true;

      return matchesSearch && matchesCategory;
    });
  }, [inventoryItems, searchTerm, selectedCategory]);

  // Handle item selection
  const handleItemSelect = (itemId: string) => {
    const isAlreadySelected = selectedEquipment.some(
      (selected) => selected.id === itemId
    );

    if (isAlreadySelected) {
      onEquipmentChange(selectedEquipment.filter((item) => item.id !== itemId));
    } else {
      onEquipmentChange([...selectedEquipment, { id: itemId, quantity: 1 }]);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    const quantity = Math.max(1, newQuantity); // Ensure minimum quantity of 1

    const updatedEquipment = selectedEquipment.map((item) =>
      item.id === itemId ? { ...item, quantity } : item
    );

    onEquipmentChange(updatedEquipment);
  };

  // Remove item from selection
  const handleRemoveItem = (itemId: string) => {
    onEquipmentChange(selectedEquipment.filter((item) => item.id !== itemId));
  };

  // Get item details by ID
  const getItemById = (itemId: string): InventoryItem | undefined => {
    return inventoryItems.find((item) => item.id === itemId);
  };

  // Get total quantity and available quantity for an item
  const getItemAvailability = (itemId: string) => {
    const item = inventoryItems.find((item) => item.id === itemId);
    if (!item || !Array.isArray(item.assets)) {
      return { total: 0, available: 0 };
    }

    const total = item.assets.length;
    const available = item.assets.filter(
      (asset: any) => asset.status === "available"
    ).length;

    return { total, available };
  };

  // Get selected equipment with full item details
  const selectedEquipmentWithDetails = useMemo(() => {
    return selectedEquipment.map((equipment) => {
      const item = getItemById(equipment.id);
      const { total, available } = getItemAvailability(equipment.id);
      const isMigratedData = equipment.id.startsWith("migrated-");

      return {
        ...equipment,
        item,
        total,
        available,
        isOverAllocated: equipment.quantity > available,
        // Handle migrated data that might not have actual inventory items
        displayName: item?.name || `Item ${equipment.id}`,
        displayCategory: item?.category || "Unknown",
        isMigratedData,
      };
    });
  }, [selectedEquipment, inventoryItems]);

  // Check if there's any migrated data
  const hasMigratedData = selectedEquipmentWithDetails.some(
    (item) => item.isMigratedData
  );

  // Helper function to get equipment summary for display
  const getEquipmentSummary = () => {
    if (selectedEquipment.length === 0) {
      return "No equipment selected";
    }

    const totalItems = selectedEquipment.reduce(
      (sum, item) => sum + item.quantity,
      0
    );
    const uniqueItems = selectedEquipment.length;

    return `${totalItems} item${
      totalItems !== 1 ? "s" : ""
    } (${uniqueItems} unique)`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-4">
        Error loading inventory items
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and filter controls at the top */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Equipment Assignment
            </h3>
            <p className="text-sm text-gray-500">{getEquipmentSummary()}</p>
          </div>
          {selectedEquipment.length > 0 && (
            <button
              onClick={() => onEquipmentChange([])}
              className="text-sm text-red-600 hover:text-red-900 px-3 py-1 rounded-md hover:bg-red-50"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Migration warning */}
        {hasMigratedData && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-yellow-800">
                  Legacy Equipment Data
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Some equipment items were migrated from the old format.
                  Consider re-adding them from the inventory for better
                  tracking.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search and filter controls */}
        <div className="space-y-4">
          {/* Search bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search for equipment..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowSearchResults(
                  e.target.value.length > 0 || !!selectedCategory
                );
              }}
            />
          </div>

          {/* Category filter */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                value={selectedCategory || ""}
                onChange={(e) => {
                  setSelectedCategory(e.target.value || null);
                  setShowSearchResults(
                    e.target.value.length > 0 || searchTerm.length > 0
                  );
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            {(searchTerm || selectedCategory) && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory(null);
                  setShowSearchResults(false);
                }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Search results dropdown */}
        {showSearchResults && (
          <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-lg max-h-64 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="p-4 text-center">
                <svg
                  className="mx-auto h-8 w-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No equipment found</p>
                <p className="text-xs text-gray-400 mt-1">
                  Try adjusting your search or category filter
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredItems.map((item) => {
                  const isSelected = selectedEquipment.some(
                    (selected) => selected.id === item.id
                  );
                  const { total, available } = getItemAvailability(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`p-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                        isSelected ? "bg-primary/5" : ""
                      }`}
                      onClick={() => handleItemSelect(item.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                            {item.images && item.images.length > 0 ? (
                              <img
                                src={item.images[0]}
                                alt={item.name}
                                className="h-10 w-10 object-cover rounded-lg"
                              />
                            ) : (
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {item.name}
                            </h4>
                            <button
                              className={`ml-2 px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                                isSelected
                                  ? "bg-red-100 text-red-800 hover:bg-red-200"
                                  : "bg-primary text-white hover:bg-primary/90"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleItemSelect(item.id);
                              }}
                            >
                              {isSelected ? "Remove" : "Add"}
                            </button>
                          </div>
                          {item.category && (
                            <p className="text-xs text-gray-500 mt-1">
                              {item.category}
                            </p>
                          )}
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {item.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {available} of {total} available
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected equipment list below */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            Selected Equipment ({selectedEquipment.length})
          </h3>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {selectedEquipment.length === 0 ? (
            <div className="p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No equipment selected
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Use the search above to add items
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {selectedEquipmentWithDetails.map((equipment) => {
                const {
                  item,
                  quantity,
                  total,
                  available,
                  isOverAllocated,
                  isMigratedData,
                  displayName,
                  displayCategory,
                } = equipment;

                if (!item && !isMigratedData) return null;

                return (
                  <div
                    key={equipment.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      isOverAllocated ? "bg-red-50" : ""
                    } ${isMigratedData ? "bg-yellow-50" : ""}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                            {item?.images && item.images.length > 0 ? (
                              <img
                                src={item.images[0]}
                                alt={displayName}
                                className="h-12 w-12 object-cover rounded-lg"
                              />
                            ) : (
                              <svg
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {displayName}
                            </h4>
                            {isMigratedData && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Legacy
                              </span>
                            )}
                          </div>
                          {displayCategory && (
                            <p className="text-xs text-gray-500 mt-1">
                              {displayCategory}
                            </p>
                          )}
                          {!isMigratedData && (
                            <p
                              className={`text-xs mt-1 ${
                                isOverAllocated
                                  ? "text-red-600 font-medium"
                                  : "text-gray-500"
                              }`}
                            >
                              {available} of {total} available
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <input
                          type="number"
                          min="1"
                          className={`w-16 border rounded-md px-2 py-1 text-sm text-center ${
                            isOverAllocated
                              ? "border-red-300 text-red-700"
                              : "border-gray-300 text-gray-700"
                          }`}
                          value={quantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              equipment.id,
                              parseInt(e.target.value) || 1
                            )
                          }
                        />
                        <button
                          onClick={() => handleRemoveItem(equipment.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove item"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
